import CloseIcon from '@mui/icons-material/Close';
import { styled, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { UseQueryResult } from '@tanstack/react-query';
import Loading from 'components/Loading';
import { LIBRARY_DEFAULT_PAGE_SIZE } from 'modules/decision/constants';
import type {
    ChartsFilters,
    ChartsQueryResponse,
    ChartsTemplatesQueryResponse,
} from 'modules/decision/types/indicators';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { concatenateSelectedValues } from '../helpers';
import IndicatorCardExplore from './IndicatorCardExplore';
import IndicatorCardLibrary from './IndicatorCardLibrary';

const StyFilterSummary = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
}));

const StyDialog = styled(Dialog, {
    shouldForwardProp: (prop) => prop !== 'forceMinHeight',
})<{ forceMinHeight: boolean }>(({ theme, forceMinHeight }) => ({
    '& .MuiDialog-paper': {
        backgroundColor: theme.palette.lightGrey,
        minHeight: forceMinHeight ? '95vh' : undefined,
    },
}));

const StyDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const StyPagination = styled(Pagination)({
    '& .MuiPagination-ul': {
        justifyContent: 'right',
    },
});

const StyTypography = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'filter',
})<{ filter: string }>(({ theme, filter }) => ({
    color: filter.length > 0 ? 'inherit' : theme.palette.mediumGrey,
    display: '-webkit-box',
    fontWeight: 510,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
}));

type SeeMoreModalProps = {
    chartsFilters: ChartsFilters;
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    title: string;
    useExploreCard?: boolean;
    displaySearch?: boolean;
    useGetCharts: (
        chartsFilters: ChartsFilters,
        page: number,
    ) => UseQueryResult<ChartsQueryResponse | ChartsTemplatesQueryResponse>;
};

const SeeMoreModal = ({
    chartsFilters,
    isOpen,
    setIsOpen,
    title,
    useExploreCard = false,
    displaySearch = true,
    useGetCharts,
}: SeeMoreModalProps): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [currentPage, setCurrentPage] = useState(1);

    const { data, isLoading } = useGetCharts(chartsFilters, currentPage);
    const activeFiltersSummary = [
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.vizType'),
            value: chartsFilters.vizTypes?.map((fltr) => fltr.name).join(', '),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.connector'),
            value: chartsFilters.integrations
                ?.map((fltr) => fltr.name)
                .join(', '),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.metric'),
            value: concatenateSelectedValues(chartsFilters.metrics),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.dimension'),
            value: concatenateSelectedValues(chartsFilters.dimensions),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.filter'),
            value: concatenateSelectedValues(chartsFilters.filters),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.aggregation'),
            value: chartsFilters.aggregations
                ?.map((fltr) => fltr.name)
                .join(', '),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.search'),
            value: chartsFilters.title ?? '',
        },
    ].filter(({ value }) => typeof value === 'string' && value.length > 0);

    const indicators = data?.charts || [];
    const count = data?.count || 0;

    const PaginationBanner = (
        <StyPagination
            color="primary"
            count={Math.ceil(count / LIBRARY_DEFAULT_PAGE_SIZE)}
            page={currentPage}
            onChange={(_event, page) => setCurrentPage(page)}
        />
    );
    const IndicatorsGrid = isLoading ? (
        <Loading />
    ) : (
        count > 0 && (
            <>
                {PaginationBanner}
                <Grid container spacing={2} sx={{ mb: 1, mt: 1 }}>
                    {indicators.map((indicator) => (
                        <Grid
                            key={indicator.key}
                            size={{ xs: 12, lg: 6 }}
                            sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                            {useExploreCard ? (
                                <IndicatorCardExplore
                                    indicator={indicator}
                                    fullWidth
                                />
                            ) : (
                                <IndicatorCardLibrary indicator={indicator} />
                            )}
                        </Grid>
                    ))}
                </Grid>
                {PaginationBanner}
            </>
        )
    );

    const handleClose = () => setIsOpen(false);

    return (
        <StyDialog
            fullWidth
            maxWidth="lg"
            open={isOpen}
            onClose={handleClose}
            forceMinHeight={isLoading}
        >
            <StyDialogTitle>
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="h5">{title}</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </div>
                {displaySearch && activeFiltersSummary.length > 0 && (
                    <StyFilterSummary>
                        <Typography
                            sx={{ mb: 2, color: theme.palette.primary.main }}
                            variant="body1Bold"
                        >
                            {t('indicators.seeMoreIndicators.filteredOn')}
                        </Typography>
                        <Grid container columns={24} spacing={1}>
                            {activeFiltersSummary.map(
                                ({ title: filterTitle, value }) => (
                                    <Fragment key={filterTitle}>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography>
                                                {filterTitle}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 9 }}>
                                            <Tooltip
                                                enterDelay={500}
                                                placement="bottom-start"
                                                title={value}
                                            >
                                                <StyTypography
                                                    filter={value ?? ''}
                                                >
                                                    {value}
                                                </StyTypography>
                                            </Tooltip>
                                        </Grid>
                                    </Fragment>
                                ),
                            )}
                        </Grid>
                    </StyFilterSummary>
                )}
            </StyDialogTitle>
            <DialogContent>{IndicatorsGrid}</DialogContent>
        </StyDialog>
    );
};

export default SeeMoreModal;
